import { useState, useRef, useEffect } from "react";
import { PaddingConfiguration } from "@/types/block-configurations";

interface PaddingControlsProps {
  value: PaddingConfiguration;
  onChange: (value: PaddingConfiguration) => void;
  label?: string;
}

const PRESETS = [0, 8, 16, 24, 40];

export function PaddingControls({ value, onChange, label = "Padding" }: PaddingControlsProps) {
  const [linked, setLinked] = useState(false);

  const set = (side: keyof PaddingConfiguration, raw: string) => {
    const n = Math.max(0, Math.min(200, parseInt(raw, 10) || 0));
    if (linked) {
      onChange({ top: n, right: n, bottom: n, left: n });
    } else {
      onChange({ ...value, [side]: n });
    }
  };

  return (
    <div className="lw-padding-controls space-y-2.5">
      <span className="text-[10px] font-medium text-foreground">{label}</span>

      {/* Box-model spatial layout */}
      <div className="grid grid-cols-[1fr_40px_1fr] grid-rows-3 items-center justify-items-center gap-1">
        {/* Row 1 — top input centred */}
        <div />
        <SideInput value={value.top} label="T" onChange={(v) => set("top", v)} />
        <div />

        {/* Row 2 — left | toggle box | right */}
        <SideInput value={value.left} label="L" onChange={(v) => set("left", v)} />
        <button
          type="button"
          onClick={() => setLinked(!linked)}
          className={`w-10 h-9 rounded border-2 flex items-center justify-center transition-colors ${
            linked
              ? "border-primary bg-primary/10 text-primary"
              : "border-dashed border-border bg-muted/40 text-muted-foreground hover:border-primary/50 hover:text-foreground"
          }`}
          title={linked ? "Unlink sides" : "Link all sides"}
        >
          {linked ? <LinkedIcon /> : <UnlinkedIcon />}
        </button>
        <SideInput value={value.right} label="R" onChange={(v) => set("right", v)} />

        {/* Row 3 — bottom input centred */}
        <div />
        <SideInput value={value.bottom} label="B" onChange={(v) => set("bottom", v)} />
        <div />
      </div>
    </div>
  );
}

interface SideInputProps {
  value: number;
  label: string;
  onChange: (v: string) => void;
}

function SideInput({ value, label, onChange }: SideInputProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={containerRef} className="relative flex flex-col items-center gap-0.5">
      <div className="flex items-center">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={0}
          max={200}
          aria-label={`${label} padding`}
          className="w-9 h-7 text-center text-xs border-y border-l border-input rounded-l bg-background focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label="Show preset values"
          className={`h-7 w-4 border border-input rounded-r flex items-center justify-center transition-colors ${
            open
              ? "bg-accent text-foreground"
              : "bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
          }`}
        >
          <ChevronIcon />
        </button>
      </div>
      {open && (
        <div className="absolute top-full left-0 z-50 mt-0.5 bg-popover border border-border rounded shadow-md py-0.5 w-full">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => { onChange(String(p)); setOpen(false); }}
              className={`block w-full text-center text-xs px-2 py-1 hover:bg-accent transition-colors ${
                value === p ? "text-primary font-medium" : "text-foreground"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      )}
      <span className="text-[9px] text-muted-foreground leading-none">{label}</span>
    </div>
  );
}

function ChevronIcon() {
  return (
    <svg className="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function LinkedIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  );
}

function UnlinkedIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.172 9.172a4 4 0 015.656 0M14.828 14.828a4 4 0 01-5.656 0m0 0L7.05 16.95M14.828 14.828L16.95 16.95M9.172 9.172L7.05 7.05m2.122 2.122L7.05 7.05m7.778 7.778l2.122 2.122M7.05 7.05A7.5 7.5 0 0116.95 16.95"
      />
    </svg>
  );
}
